import { useBoolean } from '@fluentui/react-hooks';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getGetWhitePaperReportPreviousVersionsQueryKey } from '../api/report/report';
import { useGetClaimsForCurrentUser } from '../api/user/user';
import { SignalREvent } from '../data-types/SignalREvent';
import { EventKeys } from '../utils/EventGrid.constants';
import { useNotification } from './useNotification';

export interface ITaxReturnEventHandlerContextValues {
    hasContextChanged: boolean;
    setHasContextChangedFalse: () => void;
    setNewWarningMessage: (condition: boolean, message: string) => void;
    warningMessage: string | undefined;
    clearWarningMessage: () => void;
    hasAmountChangedByAnotherUser: boolean;
    setHasAmountChangedByAnotherUserFalse: () => void;
}
export const TaxReturnEventHandlerContext = createContext<ITaxReturnEventHandlerContextValues | null>(null);

export function TaxReturnEventHandlerProvider(props: { children?: React.ReactNode; reportId: number; deleteEventCallback: () => void }) {
    const { connection, addEventHandler } = useNotification();
    const queryClient = useQueryClient();
    const [hasContextChanged, { setTrue: setHasContextChangedTrue, setFalse: setHasContextChangedFalse }] = useBoolean(false);
    const { t } = useTranslation();
    const [warningMessage, setWarningMessage] = useState<string | undefined>(undefined);
    const { data: claims } = useGetClaimsForCurrentUser();
    const [
        hasAmountChangedByAnotherUser,
        { setTrue: setHasAmountChangedByAnotherUserTrue, setFalse: setHasAmountChangedByAnotherUserFalse },
    ] = useBoolean(false);

    const onTaxReturnDeleted = (event: SignalREvent<{ DeletedKeys: number[] }>) => {
        if (event?.notificationData?.DeletedKeys?.some((key: number) => key === props.reportId)) {
            queryClient.invalidateQueries({
                queryKey: getGetWhitePaperReportPreviousVersionsQueryKey({ ReportId: props.reportId }),
            });

            props.deleteEventCallback();
        }
    };

    const onTaxReturnContextChanged = (event: SignalREvent<{ ReturnKeys: number[] }>) => {
        if (event?.notificationData?.ReturnKeys?.some((key: number) => key === props.reportId)) {
            setHasContextChangedTrue();
        }
    };

    const clearWarningMessage = () => {
        setWarningMessage(undefined);
    };

    const setNewWarningMessage = (condition: boolean, message: string) => {
        if (hasContextChanged) {
            setWarningMessage(t('calculationContextChanged').toString());
        } else if (condition) {
            setWarningMessage(message);
        }
    };

    /* This is used to update the information when the user is actively working on the return
       and another user has changed the amounts */
    const onTaxReturnAmountChanged = async (eventData: SignalREvent<{ returnKey: number }>) => {
        if (eventData?.source === 'amountChange' && eventData?.notificationData?.returnKey) {
            const returnKey = eventData.notificationData.returnKey;
            const eventUserId = eventData.userId;
            const currentUserId = claims?.userId;

            if (props.reportId === returnKey && eventUserId !== currentUserId) {
                setHasAmountChangedByAnotherUserTrue();
            }
        }
    };

    useEffect(() => {
        if (connection) {
            connection.on(EventKeys.TaxReturnDeleted, onTaxReturnDeleted);
            connection.on(EventKeys.TaxReturnContextChanged, onTaxReturnContextChanged);
            const cleanupAmountsHandler = addEventHandler(EventKeys.AmountsUpdated, onTaxReturnAmountChanged, 2);

            return () => {
                connection.off(EventKeys.TaxReturnDeleted, onTaxReturnDeleted);
                connection.off(EventKeys.TaxReturnContextChanged, onTaxReturnContextChanged);
                cleanupAmountsHandler();
            };
        }
    }, [connection, onTaxReturnDeleted, onTaxReturnContextChanged, addEventHandler]);

    return (
        <TaxReturnEventHandlerContext.Provider
            value={{
                hasContextChanged,
                setHasContextChangedFalse,
                setNewWarningMessage,
                warningMessage,
                clearWarningMessage,
                hasAmountChangedByAnotherUser,
                setHasAmountChangedByAnotherUserFalse,
            }}
        >
            {props.children}
        </TaxReturnEventHandlerContext.Provider>
    );
}

export default TaxReturnEventHandlerProvider;
